import React, { useEffect, useState } from "react";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../../../../layouts/DashboardLayout";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import useStyles from "./styles";
import PdfFileIconWithDownload from "../../../../../assets/images/Download";
import {
  getSustainabilityRiskSurveyTrackAnswers,
  getSustainabilityRiskSurveyTrackQuestions,
} from "../../../../../redux/actions";

const SustainabilityRiskAssessmentViewSurvey = () => {
  const { answerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const [showIndustrySpecific, setShowIndustrySpecific] = useState(false);

  const surveyQuestions = useSelector(
    (state) =>
      state.sustainabilityRiskAssessment
        .getSustainabilityRiskAssessementSurveyTrackQuestions.data
  );
  const surveyAnswers = useSelector(
    (state) =>
      state.sustainabilityRiskAssessment
        .getSustainabilityRiskAssessementSurveyTrackAnswers.data.answer
  );

  useEffect(() => {
    dispatch(getSustainabilityRiskSurveyTrackAnswers(answerId));
    dispatch(getSustainabilityRiskSurveyTrackQuestions(answerId));
  }, [dispatch, answerId]);

  const getAnswerByQuestionId = (questionId) => {
    const answers = surveyAnswers?.answer || [];
    const answerObj = answers.find(
      (item) => item.question_id === questionId && item.answer
    );

    return answerObj ? answerObj : {};
  };

  const renderQuestionUpdated = (question) => {
    const answerObj = getAnswerByQuestionId(question.id);
    const answer = answerObj?.answer;

    switch (question.question_type) {
      case "choose_from_option":
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingLeft={6}
          >
            <span
              style={{ fontWeight: "500", fontSize: "16px" }}
            >{`answer: `}</span>
            <span style={{ fontWeight: "200", fontSize: "16px" }}>
              {typeof answer === "string" && answer?.includes("please specify")
                ? answer?.replace(/\s*Other \(please specify\)\s*/i, " ")
                : answer}
            </span>
            {answer?.includes("please specify") && answerObj?.answer_value && (
              <span style={{ fontWeight: "200", fontSize: "16px" }}>
                {answerObj?.answer_value}
              </span>
            )}
          </Box>
        );

      case "multiselect_check_box":
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            paddingLeft={6}
          >
            <span
              style={{ fontWeight: "500", fontSize: "16px" }}
            >{`answer: `}</span>
            <span style={{ fontWeight: "200", fontSize: "16px" }}>
              {typeof answer === "string" && answer?.includes("please specify")
                ? answer?.replace(/\s*Other \(please specify\)\s*/i, " ")
                : answer}
            </span>
            {answer?.includes("please specify") && answerObj?.answer_value && (
              <span style={{ fontWeight: "200", fontSize: "16px" }}>
                {answerObj?.answer_value}
              </span>
            )}
          </Box>
        );

      case "text":
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingLeft={6}
          >
            <Box display="flex" alignItems="flex-start" paddingBottom={1}>
              <span
                style={{ fontWeight: "500", fontSize: "16px" }}
              >{`answer: `}</span>
              <span style={{ fontWeight: "200", fontSize: "16px" }}>
                {answer}
              </span>
            </Box>
          </Box>
        );

      case "upload":
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingLeft={2}
          >
            {answer?.length
              ? answer.map((item) => (
                  <a
                    href={item}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: "12px", paddingLeft: "6px" }}
                  >
                    <PdfFileIconWithDownload />
                  </a>
                ))
              : ""}
          </Box>
        );
      case "object":
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingLeft={6}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              paddingTop={3}
              paddingBottom={1}
            >
              <Typography
                variant="body1"
                sx={{ minWidth: 250, paddingLeft: 3, marginRight: 1 }}
              >
                {"answer"}:
              </Typography>
              <div>
                {Object.entries(answer).map(([key, value]) => (
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 250, paddingLeft: 3, marginRight: 1 }}
                  >
                    {`${key} : ${value}`}
                  </Typography>
                ))}
              </div>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  const groupQuestions = (questionsList) => {
    const grouped = [];
    const questionMap = new Map();

    questionsList?.forEach((question) => {
      const { id, question_id } = question;

      if (question_id && questionMap.has(question_id)) {
        questionMap.get(question_id).push(question);
      } else {
        questionMap.set(id, [question]);
      }
    });

    questionMap.forEach((group) => {
      grouped.push(group);
    });

    return grouped;
  };

  const groupedQuestions = groupQuestions(
    surveyQuestions?.supplier_generic_Q_list || []
  );

  function organizeBySectionAndSubSection(data) {
    const result = {};

    data.forEach((item) => {
      const { section, sub_section } = item[0];
      const sub_section_refactor =
        sub_section === null ? "no_section" : sub_section;
      // Ensure the section exists in the result object
      if (!result[section]) {
        result[section] = {};
      }

      // Ensure the sub_section exists in the section
      if (!result[section][sub_section_refactor]) {
        result[section][sub_section_refactor] = [];
      }

      // Add the item to the appropriate sub_section
      item.forEach((element) => {
        result[section][sub_section_refactor].push(element);
      });
    });

    return result;
  }

  function numberToRoman(num) {
    // Define the mapping of Roman numerals
    const romanMap = [
      { value: 1000, numeral: "M" },
      { value: 900, numeral: "CM" },
      { value: 500, numeral: "D" },
      { value: 400, numeral: "CD" },
      { value: 100, numeral: "C" },
      { value: 90, numeral: "XC" },
      { value: 50, numeral: "L" },
      { value: 40, numeral: "XL" },
      { value: 10, numeral: "X" },
      { value: 9, numeral: "IX" },
      { value: 5, numeral: "V" },
      { value: 4, numeral: "IV" },
      { value: 1, numeral: "I" },
    ];

    let result = "";

    // Iterate through the mapping
    for (const { value, numeral } of romanMap) {
      while (num >= value) {
        result += numeral; // Add the Roman numeral to the result
        num -= value; // Subtract the value from the number
      }
    }

    return result;
  }
  let qtsnNumber = 0;
  let subNumber = 0;
  const sectionedGenricQuestion =
    organizeBySectionAndSubSection(groupedQuestions);

  return (
    <DashboardLayout>
      <Container className={classes.mainContainer}>
        <div
          className={classes.backContainer}
          onClick={() =>
            showIndustrySpecific ? setShowIndustrySpecific(false) : navigate(-1)
          }
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <ArrowBackIosIcon />
          <Typography variant="body1">Back</Typography>
        </div>

        <Paper className={classes.container}>
          <div className={classes.paperTop}>
            <Typography variant="h6" className={classes.title}>
              Sustainability Risk Assessment Questionnaire
            </Typography>
            <Typography variant="body2" className={classes.completed}>
              <span style={{ color: "green" }}>Score</span>:{" "}
              {surveyAnswers?.total_score}
            </Typography>
          </div>

          <div className={classes.questionList}>
            {/* {renderCompanyProfile()} */}
            {Object.keys(sectionedGenricQuestion).map((group, index) => (
              <Grid key={index} className={classes.gridItem}>
                <Typography
                  fontSize={20}
                  fontWeight={600}
                  variant="h3"
                  paddingLeft={4}
                  paddingBottom={2}
                >
                  {numberToRoman(index + 1)}. {group}
                </Typography>
                {Object.keys(sectionedGenricQuestion[group]).map(
                  (subGroup, subIndex) => (
                    <div>
                      {subGroup !== "no_section" && (
                        <Typography
                          fontSize={16}
                          fontWeight={500}
                          variant="h4"
                          paddingLeft={4}
                          paddingBottom={2}
                          paddingTop={2}
                        >
                          {String.fromCharCode(96 + subIndex + 1)}. {subGroup}
                        </Typography>
                      )}
                      {sectionedGenricQuestion[group][subGroup].map(
                        (question, qu_index) => {
                          if (
                            question.question_type !== "upload" &&
                            question.question_id === null
                          ) {
                            qtsnNumber += 1;
                            subNumber = 0;
                          } else {
                            subNumber += 1;
                          }
                          return (
                            <>
                              <div className={classes.questScore}>
                                {question.question_type !== "upload" &&
                                question.question_id === null ? (
                                  <Typography
                                    fontSize={17}
                                    fontWeight={500}
                                    // variant="h4"
                                    paddingLeft={4}
                                    paddingBottom={2}
                                    className={classes.questionQr}
                                  >
                                    {qtsnNumber}. {question.question}
                                  </Typography>
                                ) : (
                                  <Typography
                                    fontSize={16}
                                    fontWeight={400}
                                    // variant="h4"
                                    paddingLeft={8}
                                    paddingBottom={2}
                                    className={classes.questionQr}
                                  >
                                    {String.fromCharCode(96 + subNumber)}.{" "}
                                    {question.question}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  color={"green"}
                                  className={classes.score}
                                >
                                  {question.question_type !== "upload" &&
                                    question.question_id === null && (
                                      <span style={{ color: "green" }}>
                                        Score:
                                      </span>
                                    )}
                                  {getAnswerByQuestionId(question.id)?.score}
                                </Typography>
                              </div>
                              <Box key={question.id} paddingLeft={4}>
                                {renderQuestionUpdated(question)}
                              </Box>
                            </>
                          );
                        }
                      )}
                    </div>
                  )
                )}
              </Grid>
            ))}
          </div>
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default SustainabilityRiskAssessmentViewSurvey;
